'use client';
import { Button, FormPage, Icon, TabButton } from '@ui/components/atoms';
import { useRouter, useSearchParams } from 'next/navigation';
import { DynamicRouteBuilder } from '@ui/constants';

import { BadgeClaimImage } from './components/badge-claim-image';
import {
  BadgeClaimSubmit,
  IBadgeClaimSubmit,
} from './components/badge-claim-info/badge-claim-info-panel/badge-claim-submit';
import { BadgeClaimNamePanel } from './components/badge-claim-info/badge-claim-name-panel';
import { BadgeClaimCreators } from './components/badge-claim-info/badge-claim-creators';
import {
  IBadgeClaimBadge,
  IBadgeClaimUser,
  IBadgeClaimCreators,
  IBadgeClaimType,
  IBadgeClaimCode,
} from './badge-claim.typings';
import { badgeClaimClassName } from './badge-claim.style';
import { BadgeClaimInfoLayout } from './components/badge-claim-info/badge-claim-info-layout';
import { BadgeClaimInfoPanel } from './components/badge-claim-info/badge-claim-info-panel/badge-claim-info-panel';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BadgeLeaderboard, IBadgeLeaderboardProps } from './components/badge-leaderboard';
import { BadgeType } from '@ui/constants';
import { Control } from 'react-hook-form';
import { useMobile } from '@ui/hooks';

type IBadgeTab = 'Info' | 'Leaderboard';

export interface IBadgeClaim extends IBadgeClaimSubmit {
  badge: IBadgeClaimBadge;
  creators: IBadgeClaimCreators;
  user: IBadgeClaimUser;
  className?: string;
  claimType?: IBadgeClaimType;
  leaderboard?: Omit<IBadgeLeaderboardProps, 'onBack' | 'avatar'>;
  currentTab?: string;
  currentSubTab?: string;
  onBack?(): void;
  gotoForum?(): void;
  openChallenge?(): void;
  isClaimExpired?: boolean;
  creatorHasCommunity?: boolean;
  hasAchieved?: boolean;
  shouldBeGrey?: boolean;
  newPostNotifications?: any;
  badgeInChallenge?: boolean;
  badgeOwned?: boolean;
  control?: Control<IBadgeClaimCode>;
  isOwned?: boolean;
  showTabUnderline?: boolean; // Control whether active tab shows underline
}

export function BadgeClaim({
  badge,
  creators,
  user,
  isClaimExpired,
  leaderboard,
  badgeState = 'default',
  claimType: badgeClaimType = 'default',
  isLoading,
  currentTab,
  shouldBeGrey,
  onBack,
  onClick,
  gotoForum,
  openChallenge,
  badgeOwned,
  hasAchieved,
  creatorHasCommunity,
  newPostNotifications,
  badgeInChallenge,
  currentSubTab,
  control,
  isOwned,
  showTabUnderline = true,
}: IBadgeClaim) {
  const className = badgeClaimClassName[badgeClaimType];
  const [tab, setTab] = useState<IBadgeTab>('Info');
  const isMobile = useMobile();
  const router = useRouter();
  const params = useSearchParams();
  const isFromCompleted = params.get('completed');
  const badgeId = params.get('badgeId');
  const isCatalogueContext = params.get('goToContent') === 'true';

  function onBadgeLeaderboard() {
    if (!leaderboard) {
      return;
    }
    setTab('Leaderboard');
  }

  function onInfo() {
    setTab('Info');
  }

  function handleCompletedChallengesClick() {
    if (creators?.creator_id) {
      router.refresh();
      router.push(
        `${DynamicRouteBuilder.challenges(creators?.name)}?badgeId=${
          badge?.badgeId
        }&subtab=Completed&t=${Date.now()}`,
      );
    }
  }

  const badgeNumber = useMemo(() => {
    if (
      !badge?.isAvailable &&
      badge.type != BadgeType.Prize &&
      badge?.type != BadgeType.Community
    ) {
      return badge?.badgeNumber;
    } else {
      return badge?.badgeNumber;
    }
  }, [badge, currentTab]);

  return (
    <FormPage className={className.container.main}>
      <BadgeClaimImage
        userName={user.name}
        badgeId={badge?.badgeId}
        badgeState={badgeState}
        name={badge.name}
        caption={badge.caption}
        shouldBeGrey={shouldBeGrey}
        image={badge.image}
        isLoggedIn={user?.isLoggedIn}
        isSubscribed={user?.isSubscribed}
        color={badge.color}
        type={badge.type}
        avatar={user.avatar}
        showAvatar={user.showAvatar}
        badgeNumber={badgeNumber}
        badgeAmount={badge.amount}
        claimType={badgeClaimType}
        claimCodeActive={badge.claim_codes_active}
        badgeOwned={badgeOwned}
        isJoiningBadge={badge.is_joining_badge}
        isCatalogueContext={isCatalogueContext}
        control={control}
        badgeType={badge?.type}
        perks={badge.perks}
        collectedPerks={badge.collectedPerks}
        onBack={onBack}
        gotoForum={gotoForum}
        creatorHasCommunity={creatorHasCommunity}
        onBadgeLeaderboard={user.showAvatar ? onBadgeLeaderboard : undefined}
        newPostNotifications={newPostNotifications}
        badgeInChallenge={badgeInChallenge}
        openChallenge={openChallenge}
        hasAchieved={hasAchieved}
        creator={creators}
        sponsors={badge?.sponsors}
        publicLink={badge?.publicLink}
        collectionAction={badge.collectionAction}
        isClaimExpired={isClaimExpired}
        isLoading={isLoading}
        onClick={onClick}
      />
      <BadgeClaimInfoLayout
        claimType={badgeClaimType}
        color={badge.color}
        user={user}
        creator={creators}
        onBack={onBack}>
        <div
          className={twMerge(
            'flex flex-col rounded-t-lg bg-white lg:flex-1',
            badgeClaimClassName[badgeClaimType].badgeDetails.infoContainer,
          )}>
          <div
            className={twMerge(
              'hidden pl-8 pr-10 lg:block',
              badgeClaimClassName[badgeClaimType].badgeDetails.tabPanel,
              user.showAvatar ? '' : 'lg:hidden',
            )}>
            <div className="flex items-center justify-between pt-10">
              {/* Tabs on the left */}
              <div className="flex gap-3">
                <TabButton
                  onClick={onInfo}
                  type={tab === 'Info' ? 'active' : 'default'}
                  className={twMerge(
                    'px-4 py-2 rounded-none bg-transparent hover:bg-transparent',
                    tab === 'Info' && 'font-semibold',
                    showTabUnderline && tab === 'Info' && 'border-b-4 border-primary',
                  )}>
                  Info
                </TabButton>
                <TabButton
                  onClick={onBadgeLeaderboard}
                  type={tab === 'Leaderboard' ? 'active' : 'default'}
                  className={twMerge(
                    'px-4 py-2 rounded-none bg-transparent hover:bg-transparent',
                    tab === 'Leaderboard' && 'font-semibold',
                    showTabUnderline && tab === 'Leaderboard' && 'border-b-4 border-primary',
                  )}>
                  Ranking
                </TabButton>
              </div>

              {/* Action buttons on the right */}
              <div className="flex gap-3">
                {isFromCompleted && (
                  <Button
                    type="primary"
                    className="whitespace-nowrap text-sm px-4 py-2 bg-primary hover:bg-primary/90"
                    onClick={handleCompletedChallengesClick}>
                    Completed Challenges
                  </Button>
                )}

                {creatorHasCommunity && (
                  <div className="relative">
                    {newPostNotifications && newPostNotifications?.unread_count > 0 && (
                      <div className="w-5 h-5 rounded-full bg-primary text-white absolute -top-1 -right-1 flex items-center justify-center text-xs z-10">
                        {newPostNotifications?.unread_count}
                      </div>
                    )}
                    <Button
                      type="primary"
                      className="whitespace-nowrap text-sm px-4 py-2 bg-black hover:bg-black/95"
                      onClick={gotoForum}>
                      <Icon type="community" className="mr-2" /> Community
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {tab === 'Leaderboard' && !!leaderboard ? (
            <BadgeLeaderboard {...leaderboard} onBack={onInfo} avatar={user.avatar} />
          ) : (
            <>
              <BadgeClaimNamePanel
                claimType={badgeClaimType}
                name={badge.name}
                caption={badge.caption}
              />
              <BadgeClaimCreators {...creators} claimType={badgeClaimType}>
                <BadgeClaimInfoPanel
                  creatorHasCommunity={creatorHasCommunity}
                  badgeOwned={badgeOwned}
                  isClaimExpired={isClaimExpired}
                  creators={creators}
                  badge={{ ...badge, badgeNumber }}
                  badgeState={badgeState}
                  claimType={badgeClaimType}
                  gotoForum={gotoForum}
                  currentTab={currentTab}
                  currentSubTab={currentSubTab}
                  control={control}
                  isOwned={isOwned}
                  claim_codes_active={badge.claim_codes_active}
                  badgeType={badge?.type}>
                  {!isMobile && (
                    <BadgeClaimSubmit
                      hasAchieved={hasAchieved}
                      sponsors={badge?.sponsors}
                      badgeType={badge?.type}
                      publicLink={badge?.publicLink}
                      collectionAction={badge.collectionAction}
                      isJoiningBadge={badge?.is_joining_badge}
                      isCatalogueContext={isCatalogueContext}
                      openChallenge={openChallenge}
                      isClaimExpired={isClaimExpired}
                      badgeState={badgeState}
                      isLoading={isLoading}
                      onClick={onClick}
                    />
                  )}
                </BadgeClaimInfoPanel>
              </BadgeClaimCreators>
            </>
          )}
        </div>
      </BadgeClaimInfoLayout>
    </FormPage>
  );
}
